import React, { useEffect, useMemo, useState } from "react";
import Aside from "../components/Aside";

import { DayPicker } from "react-day-picker";
import { ptBR } from "date-fns/locale";
import { format, parseISO } from "date-fns";
import "react-day-picker/dist/style.css";

import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

import { MdEdit, MdDelete, MdAdd } from "react-icons/md";
import { database } from "../services/firebase";
import { ref, onValue, set, remove } from "firebase/database";

const Config = () => {
  const isMobile = useMediaQuery("(max-width:900px)");
  const ASIDE_W = 260;
  const MOBILE_TOPBAR_H = 64;

  const [diasConfig, setDiasConfig] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);

  const [form, setForm] = useState({
    date: null,
    almoco: false,
    jantar: false,
  });

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");

  const showSnack = (msg) => {
    setSnackMsg(msg);
    setSnackOpen(true);
  };

  // =========================
  // CARREGA DIAS ATÍPICOS
  // =========================
  useEffect(() => {
    const refDias = ref(database, "config/dias");
    const unsub = onValue(refDias, (snap) => {
      setDiasConfig(snap.exists() ? snap.val() : {});
    });
    return () => unsub();
  }, []);

  const diasList = useMemo(() => {
    return Object.entries(diasConfig)
      .map(([date, cfg]) => ({ date, ...cfg }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [diasConfig]);

  // =========================
  // HELPERS
  // =========================
  const disableInvalidDays = (day) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // bloqueia datas passadas
    if (day < today) return true;

    // permite apenas segunda-feira
    return day.getDay() !== 1;
  };

  const openCreate = () => {
    setEditingKey(null);
    setForm({
      date: null,
      almoco: true,
      jantar: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingKey(item.date);
    setForm({
      date: parseISO(item.date),
      almoco: item.almoco === true,
      jantar: item.jantar === true,
    });
    setDialogOpen(true);
  };

  const saveDia = async () => {
    
    if (!form.date) {
      showSnack("Selecione uma data.");
      return;
    }

    const key = format(form.date, "yyyy-MM-dd");

    await set(ref(database, `config/dias/${key}`), {
      almoco: form.almoco,
      jantar: form.jantar,
    });

    setDialogOpen(false);
    showSnack("Data atípica salva com sucesso!");
  };

  const deleteDia = async (date) => {
    await remove(ref(database, `config/dias/${date}`));
    showSnack("Data removida!");
  };

  const resumoDia = (almoco, jantar) => {
    if (almoco && jantar) {
      return {
        label: "Aberto",
        color: "success",
      };
    }

    if (almoco && !jantar) {
      return {
        label: "Somente Almoço",
        color: "warning",
      };
    }

    if (!almoco && jantar) {
      return {
        label: "Somente Jantar",
        color: "warning",
      };
    }

    return {
      label: "Fechado",
      color: "error",
    };
  };

  return (
    <>
      <Aside />

      <Box
        sx={{
          minHeight: "100vh",
          background: "var(--offwhite)",
          ml: isMobile ? 0 : `${ASIDE_W}px`,
          pt: isMobile ? `${MOBILE_TOPBAR_H + 16}px` : 3,
          pb: 4,
        }}
      >
        <Container maxWidth={false} disableGutters sx={{ px: { xs: 2, md: 3 } }}>
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Typography variant="h5" sx={{ fontWeight: 1000, color: "var(--azulEscuro)" }}>
              Datas Atípicos (Aberturas e Fechamentos Excepcionais)
            </Typography>

            <Button
              variant="contained"
              startIcon={<MdAdd />}
              onClick={openCreate}
              sx={{
                backgroundColor: "var(--azulEscuro)",
                fontWeight: 900,
                "&:hover": { backgroundColor: "rgba(7,23,57,0.9)" },
              }}
            >
              Adicionar Data
            </Button>
          </Stack>

          <Paper elevation={0} sx={{ border: "1px solid var(--input-border)", borderRadius: 3 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 900 }}>Data</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Resumo do Dia</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Almoço</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Jantar</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900 }}>
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {diasList.map((d) => (
                    <TableRow key={d.date} hover>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {format(parseISO(d.date), "dd/MM/yyyy")}
                      </TableCell>

                      <TableCell>
                        {(() => {
                          const r = resumoDia(d.almoco, d.jantar);

                          return (
                            <Chip
                              label={r.label}
                              color={r.color}
                              size="small"
                              sx={{
                                fontWeight: 900,
                                minWidth: 120,
                                justifyContent: "center",
                              }}
                            />
                          );
                        })()}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={d.almoco ? "Aberto" : "Fechado"}
                          color={d.almoco ? "success" : "default"}
                          size="small"
                          sx={{ fontWeight: 900 }}
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={d.jantar ? "Aberto" : "Fechado"}
                          color={d.jantar ? "success" : "default"}
                          size="small"
                          sx={{ fontWeight: 900 }}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton onClick={() => openEdit(d)}>
                            <MdEdit />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Remover">
                          <IconButton
                            onClick={() => deleteDia(d.date)}
                            sx={{ color: "var(--vermelho)" }}
                          >
                            <MdDelete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}

                  {diasList.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography sx={{ fontWeight: 700, color: "var(--cinza)" }}>
                          Nenhuma data cadastrado.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Container>
      </Box>

      {/* DIALOG */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900, color: "var(--azulEscuro)" }}>
          {editingKey ? "Editar Data" : "Adicionar Data"}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>

            {!editingKey ? (
              <>
                <Typography fontWeight={700}>Selecione a Data</Typography>

                <DayPicker
                  mode="single"
                  locale={ptBR}
                  selected={form.date}
                  onSelect={(d) => setForm((p) => ({ ...p, date: d }))}
                />

                <Divider />
              </>
            ) : false}

            <FormControlLabel
              control={
                <Checkbox
                  checked={form.almoco}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, almoco: e.target.checked }))
                  }
                />
              }
              label="Almoço"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={form.jantar}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, jantar: e.target.checked }))
                  }
                />
              }
              label="Jantar"
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ fontWeight: 900 }}>
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={saveDia}
            sx={{
              backgroundColor: "var(--azulEscuro)",
              fontWeight: 900,
              "&:hover": { backgroundColor: "rgba(7,23,57,0.9)" },
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity="success"
          sx={{ fontSize: 16, fontWeight: 700 }}
        >
          {snackMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Config;
